import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

/**
 * V8.1 · Wallet page.
 * Read-only view of the tenant's credit balance and immutable ledger.
 * Purchase / grant flows live in Billing + Admin (Product Studio, later).
 */
export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [w, l] = await Promise.all([
          api.get("/wallet"),
          api.get("/wallet/ledger?limit=100"),
        ]);
        setWallet(w.data);
        setEntries(l.data.entries || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="wallet-page" className="space-y-6">
      <PageHeader
        title="Credit Wallet"
        subtitle="Balance et historique immuable des crédits Orixa."
      />

      {loading && (
        <div className="text-sm text-muted-foreground">Loading wallet…</div>
      )}

      {wallet && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard testid="wallet-available" label="Available" value={wallet.balance.available} accent="text-primary" />
          <StatCard testid="wallet-reserved" label="Reserved" value={wallet.balance.reserved} accent="text-amber-700" />
          <StatCard testid="wallet-committed" label="Committed" value={wallet.balance.committed} accent="text-slate-700" />
        </div>
      )}

      <div data-testid="wallet-ledger" className="bg-card border border-border rounded-2xl p-4 sm:p-6">
        <div className="text-overline text-muted-foreground mb-3">Ledger (most recent first)</div>
        {entries.length === 0 && (
          <div className="text-sm text-muted-foreground">No ledger entries yet.</div>
        )}
        {entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-2 pr-4">When</th>
                  <th className="text-left py-2 pr-4">Kind</th>
                  <th className="text-right py-2 pr-4">Amount</th>
                  <th className="text-left py-2 pr-4">Reason</th>
                  <th className="text-left py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`ledger-row-${e.id}`} className="border-t border-border">
                    <td className="py-2 pr-4 text-slate-500">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 pr-4 font-medium">{e.kind}</td>
                    <td className={`py-2 pr-4 text-right font-mono ${e.amount > 0 ? "text-emerald-700" : e.amount < 0 ? "text-red-700" : "text-slate-500"}`}>
                      {e.amount > 0 ? `+${e.amount}` : e.kind === "RESERVATION" ? `(${e.reserve_amount})` : e.amount}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{e.reason || e.purpose || "—"}</td>
                    <td className="py-2 pr-4"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ testid, label, value, accent }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-2xl p-5">
      <div className="text-overline text-muted-foreground">{label}</div>
      <div className={`text-3xl font-serif mt-2 ${accent}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">credits</div>
    </div>
  );
}
